import React, { useState } from 'react';
import {
    useUpdateStageStatusMutation,
    useUpdatePortalProjectMutation,
} from '../../../redux/api/compassPortal';
import { errMsg } from './bits';

// Validation / Modelling / Delivery panels. These stages are status milestones (the
// heavy work — validation, model fitting, delivery push — happens in the external
// Compass pipeline), but MS's tweaks/notes and the delivery location surface here.
// Mutations invalidate PortalProjects, so the parent's project query refreshes itself.

const STAGE_COPY = {
    'data-validation': {
        what: 'The team validates the cleaned data against the client rule set and expectations. Sign-off here confirms the data is model-ready.',
    },
    'data-modelling': {
        what: 'The first marketing-mix model is fitted in the Compass pipeline. Marketing Science tunes adstock, saturation and channel selection; their choices are recorded below and the portal reflects the run status.',
    },
    'compass-delivery': {
        what: 'Finished model outputs are delivered to the Compass MMM Reports shared Drive. This panel links to where they live.',
    },
};

const NEXT = {
    'data-validation': 'data-modelling',
    'data-modelling': 'compass-delivery',
    'compass-delivery': null,
};

export default function StagePanel({ project, stageId, stageName, onBack, onComplete }) {
    const stage = (project.stages || []).find((s) => s.id === stageId) || { id: stageId, status: 'not-started' };
    const [notes, setNotes] = useState('');
    const [err, setErr] = useState(null);
    const [folder, setFolder] = useState(project.google_folder_url || '');
    const isDelivery = stageId === 'compass-delivery';
    const copy = STAGE_COPY[stageId] || { what: '' };

    const [updateStageStatus, { isLoading: busy }] = useUpdateStageStatusMutation();
    const [updateProject, { isLoading: savingFolder }] = useUpdatePortalProjectMutation();

    const setStatus = async (status) => {
        setErr(null);
        try {
            await updateStageStatus({ projectId: project.project_id, stage_id: stageId, status }).unwrap();
            if (status === 'complete') onComplete?.(stageId, NEXT[stageId]);
        } catch (e) { setErr(errMsg(e)); }
    };

    const saveFolder = async () => {
        setErr(null);
        try { await updateProject({ projectId: project.project_id, google_folder_url: folder.trim() }).unwrap(); }
        catch (e) { setErr(errMsg(e)); }
    };

    return (
        <>
            <button className="cp-back-btn" onClick={onBack}>← Back to projects</button>

            <div className="cp-card" style={{ padding: 24, maxWidth: 760 }}>
                <p className="cp-section-label">{stageName}</p>
                <p className="cp-sub" style={{ marginTop: 0 }}>{copy.what}</p>
                {stage.due_date && <p className="cp-due">Due {stage.due_date}</p>}

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '16px 0' }}>
                    <span className="cp-uploaded-by">Status:</span>
                    <select value={stage.status} disabled={busy} onChange={(e) => setStatus(e.target.value)} style={sel}>
                        <option value="not-started">Not started</option>
                        <option value="in-progress">In progress</option>
                        <option value="blocked">Blocked</option>
                        <option value="complete">Complete</option>
                    </select>
                </div>

                {isDelivery && (
                    <div style={{ margin: '16px 0', paddingTop: 16, borderTop: '1px solid rgba(20,22,21,0.1)' }}>
                        <p className="cp-section-label">Delivered outputs</p>
                        {project.google_folder_url ? (
                            <a className="cp-btn cp-btn--primary cp-btn--sm" href={project.google_folder_url} target="_blank" rel="noreferrer">
                                Open the Compass MMM Reports folder →
                            </a>
                        ) : (
                            <p className="cp-sub" style={{ marginTop: 0 }}>No delivery folder set yet. Add the shared-Drive link so outputs are one click away.</p>
                        )}
                        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                            <input value={folder} onChange={(e) => setFolder(e.target.value)} placeholder="https://drive.google.com/…"
                                style={{ ...sel, flex: 1, minWidth: 260 }} />
                            <button className="cp-btn cp-btn--ghost cp-btn--sm" disabled={savingFolder} onClick={saveFolder}>
                                {savingFolder ? 'Saving…' : 'Save folder'}
                            </button>
                        </div>
                    </div>
                )}

                {(stageId === 'data-modelling' || stageId === 'data-validation') && (
                    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(20,22,21,0.1)' }}>
                        <p className="cp-section-label">MS notes for this stage</p>
                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                            placeholder="e.g. Raised Meta adstock to 0.7; dropped OOH (too sparse)."
                            style={{ ...sel, width: '100%', resize: 'vertical' }} />
                        <p className="cp-sub" style={{ fontSize: 12 }}>Notes are for the record; model fitting itself runs in the Compass pipeline.</p>
                    </div>
                )}

                {err && <p style={{ color: 'var(--co-neg)', fontSize: 13 }}>{err}</p>}
            </div>
        </>
    );
}

const sel = {
    padding: '8px 12px', borderRadius: 'var(--co-radius-sm)', border: '1px solid rgba(20,22,21,0.2)',
    font: 'inherit', fontSize: 14, background: '#fff',
};
